/* ══════════════════════════════════════════════
 *  AutomationEditor — Full-page canvas editor
 *  React Flow canvas + NodePalette + PropertiesPanel
 *  Phase 4: Tabs (Canvas / History), Test Run, Validation
 * ══════════════════════════════════════════════ */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  type Connection,
  type Node,
  type Edge,
  type NodeTypes,
  type EdgeTypes,
  type IsValidConnection,
  type ReactFlowInstance,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import css from './AutomationEditor.module.css';
import {
  useAutomation,
  useNodeDefs,
  useSaveCanvas,
  useTestRun,
  useValidateAutomation,
  useAutomationExecutions,
} from '../../hooks/useAutomations';
import { useToast } from '../../hooks/useToast';
import CanvasToolbar from './components/CanvasToolbar';
import NodePalette from './components/NodePalette';
import PropertiesPanel from './components/PropertiesPanel';
import ExecutionHistory from './components/ExecutionHistory';
import TestRunPanel from './components/TestRunPanel';
import TriggerNode from './components/nodes/TriggerNode';
import ActionNode from './components/nodes/ActionNode';
import ConditionNode from './components/nodes/ConditionNode';
import ControlNode from './components/nodes/ControlNode';
import EndNode from './components/nodes/EndNode';
import ConditionEdge from './components/edges/ConditionEdge';
import type { AutomationNodeDef, TestRunResult, ValidationResult } from '../../types/automations';

const NODE_TYPES: NodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  control: ControlNode,
  end: EndNode,
};

const EDGE_TYPES: EdgeTypes = {
  condition: ConditionEdge,
};

function AutomationEditorInner() {
  const { id } = useParams<{ id: string }>();
  const automationId = id ? parseInt(id) : null;

  const { data: automation, isLoading } = useAutomation(automationId);
  const { data: nodeDefs = [] } = useNodeDefs();
  const saveCanvas = useSaveCanvas();
  const testRun = useTestRun();
  const validateMutation = useValidateAutomation();
  const { data: executions = [] } = useAutomationExecutions(automationId);
  const { showToast } = useToast();

  const [nodes, setNodes, onNodesChange] = useNodesState([] as Node[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const initialLoadDone = useRef(false);

  // Dirty-state tracking: snapshot of nodes/edges at last save or load
  const savedSnapshot = useRef<string>('');

  const computeSnapshot = useCallback((n: Node[], e: Edge[]) => {
    // Lightweight serialisation — position + data + connections
    const ns = n.map((nd) => ({ id: nd.id, x: nd.position.x, y: nd.position.y, d: nd.data }));
    const es = e.map((ed) => ({
      s: ed.source,
      t: ed.target,
      sh: ed.sourceHandle,
      th: ed.targetHandle,
    }));
    return JSON.stringify({ ns, es });
  }, []);

  const isDirty = useMemo(
    () => savedSnapshot.current !== '' && computeSnapshot(nodes, edges) !== savedSnapshot.current,
    [nodes, edges, computeSnapshot],
  );

  // Phase 4 state
  const [activeTab, setActiveTab] = useState<'canvas' | 'history'>('canvas');
  const [showTestRun, setShowTestRun] = useState(false);
  const [testRunResult, setTestRunResult] = useState<TestRunResult | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Count failed executions for badge
  const failedCount = useMemo(
    () => executions.filter((e) => e.status === 'failed').length,
    [executions],
  );

  // Build a lookup map for node defs
  const nodeDefMap = useMemo(() => {
    const map: Record<string, AutomationNodeDef> = {};
    for (const nd of nodeDefs) map[nd.key] = nd;
    return map;
  }, [nodeDefs]);

  // Load automation data into React Flow state
  useEffect(() => {
    if (!automation || initialLoadDone.current) return;

    const rfNodes: Node[] = (automation.nodes || []).map((n) => ({
      id: String(n.id),
      type: n.category || nodeDefMap[n.node_def_key]?.category || 'action',
      position: { x: n.position_x, y: n.position_y },
      data: {
        label: n.label || n.def_name || n.node_def_key,
        node_def_key: n.node_def_key,
        config: n.config || {},
        icon: n.icon || nodeDefMap[n.node_def_key]?.icon,
        color: n.color || nodeDefMap[n.node_def_key]?.color,
        config_schema: n.config_schema || nodeDefMap[n.node_def_key]?.config_schema,
        category: n.category || nodeDefMap[n.node_def_key]?.category,
        def_name: n.def_name || nodeDefMap[n.node_def_key]?.name,
      },
    }));

    const rfEdges: Edge[] = (automation.edges || []).map((e) => {
      const sourceNode = rfNodes.find((n) => n.id === String(e.source_node_id));
      const isConditionEdge = sourceNode?.type === 'condition';
      return {
        id: `e${e.source_node_id}-${e.target_node_id}`,
        source: String(e.source_node_id),
        target: String(e.target_node_id),
        sourceHandle: e.source_handle || 'default',
        targetHandle: e.target_handle || 'default',
        animated: true,
        ...(isConditionEdge ? { type: 'condition' } : {}),
      };
    });

    setNodes(rfNodes);
    setEdges(rfEdges);
    savedSnapshot.current = computeSnapshot(rfNodes, rfEdges);
    initialLoadDone.current = true;
  }, [automation, nodeDefMap, setNodes, setEdges, computeSnapshot]);

  // Handle new connections
  const onConnect = useCallback(
    (params: Connection) => {
      const sourceNode = nodes.find((n) => n.id === params.source);
      const isConditionEdge = sourceNode?.type === 'condition';
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: true,
            ...(isConditionEdge ? { type: 'condition' } : {}),
          },
          eds,
        ),
      );
    },
    [setEdges, nodes],
  );

  // Prevent invalid connections: self-loops, cycles, edges into trigger nodes
  const isValidConnection: IsValidConnection = useCallback(
    (connection) => {
      // No self-loops
      if (connection.source === connection.target) return false;

      // Trigger nodes cannot be targets
      const targetNode = nodes.find((n) => n.id === connection.target);
      if (targetNode?.type === 'trigger') return false;

      // Cycle detection via DFS from target → see if we can reach source
      const visited = new Set<string>();
      const stack = [connection.target!];
      while (stack.length > 0) {
        const current = stack.pop()!;
        if (current === connection.source) return false; // cycle detected
        if (visited.has(current)) continue;
        visited.add(current);
        for (const edge of edges) {
          if (edge.source === current) {
            stack.push(edge.target);
          }
        }
      }

      return true;
    },
    [nodes, edges],
  );

  // Warn before leaving with unsaved changes (browser tab close / refresh)
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Clear selection when nodes are deleted (Delete key, Backspace, or programmatic)
  const onNodesDelete = useCallback((deleted: Node[]) => {
    const deletedIds = new Set(deleted.map((n) => n.id));
    setSelectedNodeId((prev) => (prev && deletedIds.has(prev) ? null : prev));
  }, []);

  // Programmatic delete from properties panel
  const onDeleteNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedNodeId((prev) => (prev === nodeId ? null : prev));
    },
    [setNodes, setEdges],
  );

  // Handle node click → select for properties panel
  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNodeId(node.id);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  // Drag & drop from palette
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const defKey = event.dataTransfer.getData('application/automation-node-def');
      if (!defKey || !reactFlowInstance) return;

      const def = nodeDefMap[defKey];
      if (!def) return;

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode: Node = {
        id: `new_${Date.now()}`,
        type: def.category,
        position,
        data: {
          label: def.name,
          node_def_key: def.key,
          config: {},
          icon: def.icon,
          color: def.color,
          config_schema: def.config_schema,
          category: def.category,
          def_name: def.name,
        },
      };

      setNodes((nds) => [...nds, newNode]);
    },
    [reactFlowInstance, nodeDefMap, setNodes],
  );

  // Update node config from properties panel
  const onUpdateNodeConfig = useCallback(
    (nodeId: string, config: Record<string, unknown>) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, config } } : n)),
      );
    },
    [setNodes],
  );

  // Update node label from properties panel
  const onUpdateNodeLabel = useCallback(
    (nodeId: string, label: string) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, label } } : n)),
      );
    },
    [setNodes],
  );

  // Validation handler (called from CanvasToolbar before toggle, and before save)
  const handleValidate = useCallback(async (): Promise<ValidationResult | null> => {
    if (!automationId) return null;
    try {
      const result = await validateMutation.mutateAsync(automationId);
      setValidationErrors(result.errors);
      return result;
    } catch {
      showToast('Validation failed', 'error');
      return null;
    }
  }, [automationId, validateMutation, showToast]);

  // Save canvas to backend
  const handleSave = useCallback(async () => {
    if (!automationId) return;

    // Run validation before saving — warn but don't block
    if (automation?.is_active) {
      const validation = await handleValidate();
      if (validation && !validation.valid) {
        showToast('Warning: canvas has validation issues', 'error');
      }
    }

    const payload = {
      nodes: nodes.map((n) => ({
        id: n.id,
        node_def_key: n.data.node_def_key as string,
        label: (n.data.label as string) || undefined,
        config: (n.data.config as Record<string, unknown>) || {},
        position_x: n.position.x,
        position_y: n.position.y,
      })),
      edges: edges.map((e) => ({
        source: e.source,
        target: e.target,
        source_handle: e.sourceHandle || 'default',
        target_handle: e.targetHandle || 'default',
      })),
    };

    try {
      const saved = await saveCanvas.mutateAsync({ id: automationId, canvas: payload });
      showToast('Canvas saved', 'success');

      // Remap nodes/edges to server-generated IDs so we stay in sync
      const idMap = new Map<string, string>();
      const serverNodes: Node[] = (saved.nodes || []).map((n) => {
        const sId = String(n.id);
        // Match by position (x,y) + def_key to find the original client node
        const origNode = nodes.find(
          (cn) =>
            cn.data.node_def_key === n.node_def_key &&
            Math.abs(cn.position.x - n.position_x) < 1 &&
            Math.abs(cn.position.y - n.position_y) < 1,
        );
        if (origNode) idMap.set(origNode.id, sId);

        return {
          id: sId,
          type: n.category || nodeDefMap[n.node_def_key]?.category || 'action',
          position: { x: n.position_x, y: n.position_y },
          data: {
            label: n.label || n.def_name || n.node_def_key,
            node_def_key: n.node_def_key,
            config: n.config || {},
            icon: n.icon || nodeDefMap[n.node_def_key]?.icon,
            color: n.color || nodeDefMap[n.node_def_key]?.color,
            config_schema: n.config_schema || nodeDefMap[n.node_def_key]?.config_schema,
            category: n.category || nodeDefMap[n.node_def_key]?.category,
            def_name: n.def_name || nodeDefMap[n.node_def_key]?.name,
          },
        };
      });

      const serverEdges: Edge[] = (saved.edges || []).map((e) => {
        const srcNode = serverNodes.find((n) => n.id === String(e.source_node_id));
        const isCondEdge = srcNode?.type === 'condition';
        return {
          id: `e${e.source_node_id}-${e.target_node_id}`,
          source: String(e.source_node_id),
          target: String(e.target_node_id),
          sourceHandle: e.source_handle || 'default',
          targetHandle: e.target_handle || 'default',
          animated: true,
          ...(isCondEdge ? { type: 'condition' } : {}),
        };
      });

      setNodes(serverNodes);
      setEdges(serverEdges);
      savedSnapshot.current = computeSnapshot(serverNodes, serverEdges);

      // Update selected node ID if it was remapped
      setSelectedNodeId((prev) => {
        if (!prev) return null;
        return idMap.get(prev) || prev;
      });
    } catch {
      showToast('Failed to save canvas', 'error');
    }
  }, [
    automationId,
    nodes,
    edges,
    saveCanvas,
    showToast,
    nodeDefMap,
    setNodes,
    setEdges,
    computeSnapshot,
    automation,
    handleValidate,
  ]);

  // Test run handler
  const handleTestRun = useCallback(async () => {
    if (!automationId) return;
    try {
      const result = await testRun.mutateAsync(automationId);
      setTestRunResult(result);
    } catch {
      showToast('Test run failed', 'error');
    }
  }, [automationId, testRun, showToast]);

  // Selected node for properties panel
  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId],
  );

  if (isLoading) {
    return (
      <div className={css.loadingWrapper}>
        <div className={css.spinner}>Loading...</div>
      </div>
    );
  }

  if (!automation) {
    return (
      <div className={css.loadingWrapper}>
        <div className={css.spinner}>Automation not found</div>
      </div>
    );
  }

  return (
    <div className={css.editorLayout}>
      <CanvasToolbar
        automation={automation}
        isSaving={saveCanvas.isPending}
        isDirty={isDirty}
        onSave={handleSave}
        onValidate={handleValidate}
        onTestRun={() => {
          setShowTestRun(true);
          handleTestRun();
        }}
      />

      {/* Validation errors banner */}
      {validationErrors.length > 0 && (
        <div className={css.validationErrors}>
          <div className={css.validationTitle}>Validation Issues</div>
          <ul className={css.validationList}>
            {validationErrors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Tab bar */}
      <div className={css.tabBar}>
        <button
          className={`${css.tab} ${activeTab === 'canvas' ? css.tabActive : ''}`}
          onClick={() => setActiveTab('canvas')}
        >
          Canvas
        </button>
        <button
          className={`${css.tab} ${activeTab === 'history' ? css.tabActive : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Execution History
          {failedCount > 0 && <span className={css.tabBadge}>{failedCount}</span>}
        </button>
      </div>

      {/* Canvas tab */}
      {activeTab === 'canvas' && (
        <>
          <div className={css.editorBody}>
            <NodePalette nodeDefs={nodeDefs} />

            <div className={css.canvasWrapper} ref={reactFlowWrapper}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                isValidConnection={isValidConnection}
                onNodeClick={onNodeClick}
                onPaneClick={onPaneClick}
                onNodesDelete={onNodesDelete}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onInit={setReactFlowInstance}
                nodeTypes={NODE_TYPES}
                edgeTypes={EDGE_TYPES}
                fitView
                deleteKeyCode="Delete"
                snapToGrid
                snapGrid={[16, 16]}
              >
                <Background gap={16} size={1} />
                <Controls />
                <MiniMap
                  nodeColor={(n) => {
                    const color = n.data?.color as string;
                    return color || '#888';
                  }}
                  maskColor="rgba(0,0,0,0.1)"
                />
              </ReactFlow>
            </div>

            <PropertiesPanel
              node={selectedNode}
              nodeDefMap={nodeDefMap}
              onUpdateConfig={onUpdateNodeConfig}
              onUpdateLabel={onUpdateNodeLabel}
              onDeleteNode={onDeleteNode}
            />
          </div>

          {/* Test Run drawer at bottom of canvas */}
          {showTestRun && (
            <TestRunPanel
              result={testRunResult}
              isRunning={testRun.isPending}
              onRun={handleTestRun}
              onClose={() => {
                setShowTestRun(false);
                setTestRunResult(null);
              }}
            />
          )}
        </>
      )}

      {/* History tab */}
      {activeTab === 'history' && automationId && <ExecutionHistory automationId={automationId} />}
    </div>
  );
}

export default function AutomationEditor() {
  return (
    <ReactFlowProvider>
      <AutomationEditorInner />
    </ReactFlowProvider>
  );
}
